import { createCamera } from './camera.js';
import { createEngagement } from './engagement.js';
import { createPointerBridge } from './pointer-bridge.js';
import { createSceneCursor } from './cursor.js';
import { hasFinePointer } from './tier.js';

/**
 * Assembles the scene layers and wires the camera, pointer adapter,
 * engagement model and custom cursor together.
 *
 * The scene is built inside the mount block and never touches html/body or the
 * viewport, so it can sit between other blocks on a Tilda page.
 */

function layer(className, tag = 'div') {
  const element = document.createElement(tag);
  element.className = className;
  return element;
}

/**
 * @param {object} options
 * @param {HTMLElement} options.viewport - container the scene is rendered into
 * @param {'desktop'|'tablet'|'phone'} options.tier
 * @param {boolean} options.standalone - true on the local preview page
 * @param {boolean} [options.skipIntro] - rebuilds of an already-seen scene
 *   start settled instead of replaying the fly-in
 * @param {string} options.hint - short affordance text
 */
export function createScene({ viewport, tier, standalone, skipIntro = false, hint }) {
  const scene = layer('scene');
  scene.dataset.tier = tier;

  const surface = layer('scene__surface');
  surface.setAttribute('aria-hidden', 'true');

  const vignette = layer('scene__vignette');
  vignette.setAttribute('aria-hidden', 'true');

  const camera = layer('scene__camera');
  const stage = layer('stage');
  const shadow = layer('stage__shadow');
  shadow.setAttribute('aria-hidden', 'true');

  // The remaining leaves on either side, and the binding between them. These
  // are what stop an open spread reading as two white rectangles.
  const edgeLeft = layer('stage__edge stage__edge--left');
  const edgeRight = layer('stage__edge stage__edge--right');
  // The binding is split into two halves so each side can be clipped
  // independently while a leaf turns (see syncGutterMask).
  const gutter = layer('stage__gutter');
  const gutterHalves = {
    left: layer('stage__gutter-half stage__gutter-half--left'),
    right: layer('stage__gutter-half stage__gutter-half--right')
  };
  gutter.append(gutterHalves.left, gutterHalves.right);
  for (const element of [edgeLeft, edgeRight, gutter]) element.setAttribute('aria-hidden', 'true');

  const bookHost = layer('book-flip-host');

  stage.append(shadow, edgeLeft, edgeRight, bookHost, gutter);
  camera.append(stage);

  const hintText = layer('scene__hint', 'p');
  hintText.textContent = hint;

  scene.append(surface, vignette, camera, hintText);
  viewport.replaceChildren(scene);

  let currentTier = tier;
  let cameraController = null;
  let engagement = null;
  let bridge = null;
  let cursor = null;

  function startCamera() {
    if (cameraController) return;
    cameraController = createCamera({
      root: scene,
      tier: currentTier,
      skipIntro,
      onIntroEnd: () => {
        scene.dataset.ready = 'true';
      }
    });
  }

  function startEngagement() {
    if (engagement) return;
    engagement = createEngagement({
      scene,
      startEngaged: standalone,
      onZoom: (delta) => cameraController.zoomBy(delta)
    });
  }

  function startCursor() {
    if (cursor || !hasFinePointer()) return;
    cursor = createSceneCursor({ scene });
  }

  // --- gutter mask ---------------------------------------------------------
  // The sewing thread sits at z-index 4: above pages lying flat and above the
  // page being revealed under a turning leaf (engine z 1 and 3), below the
  // airborne fold itself (engine z 5). One case remains wrong at that height:
  // the flat, not-yet-lifted remainder of the turning page is an engine z-1
  // item, so the thread would paint on top of the very paper the reader is
  // peeling. The engine already clips the revealed page (z 3) with a polygon
  // whose edge IS the fold line, so while a leaf is off the block that same
  // polygon is mirrored onto the thread's half: the thread shows only where
  // the underlying spread is actually exposed.
  let maskFrame = 0;

  // The binding must live INSIDE the engine's .stf__block: the block has
  // `perspective`, which makes it a stacking context, so a sibling can never
  // slot between the engine's own leaves (z 1/3/5) — it is always either over
  // or under the whole book. Inside the block, z-index 4 lands exactly where
  // the thread belongs. Re-checked on every use in case the engine rebuilds
  // its DOM on resize.
  function placeGutter() {
    const block = bookHost.querySelector('.stf__block');
    if (block && gutter.parentElement !== block) block.append(gutter);
  }

  function syncGutterMask() {
    placeGutter();
    const centre = bookHost.offsetWidth / 2;
    const clips = { left: '', right: '' };

    for (const item of bookHost.querySelectorAll('.stf__item')) {
      if (item.style.display === 'none' || item.style.zIndex !== '3') continue;
      const polygon = /polygon\(([^)]+)\)/.exec(item.style.clipPath);
      const shift = /translate3d\((-?[\d.e]+)px/.exec(item.style.transform);
      if (!polygon || !shift) continue;

      const tx = parseFloat(shift[1]);
      const side = tx < centre / 2 ? 'left' : 'right';
      const originX = side === 'left' ? centre - gutterHalves.left.offsetWidth : centre;
      const points = polygon[1].split(',').map((pair) => {
        const [x, y] = pair.trim().split(/\s+/).map(parseFloat);
        return `${(x + tx - originX).toFixed(2)}px ${y.toFixed(2)}px`;
      });
      clips[side] = `polygon(${points.join(', ')})`;
    }

    gutterHalves.left.style.clipPath = clips.left;
    gutterHalves.right.style.clipPath = clips.right;
  }

  function runGutterMask() {
    syncGutterMask();
    maskFrame = requestAnimationFrame(runGutterMask);
  }

  function stopGutterMask() {
    cancelAnimationFrame(maskFrame);
    maskFrame = 0;
    gutterHalves.left.style.clipPath = '';
    gutterHalves.right.style.clipPath = '';
  }

  return {
    scene,
    bookHost,

    /**
     * Camera, engagement and cursor without the page adapter. Used by the
     * closed state, which handles its own pointer gestures.
     */
    attachStatic() {
      startCamera();
      startEngagement();
      startCursor();
      return { camera: cameraController, engagement };
    },

    /**
     * Called once StPageFlip has loaded its pages.
     *
     * @param {import('page-flip').PageFlip} pageFlip
     * @param {{ onPrevAtStart?: () => void }} [handlers]
     */
    attach(pageFlip, handlers = {}) {
      startCamera();
      startEngagement();
      startCursor();
      placeGutter();

      bridge = createPointerBridge({
        scene,
        pageFlip,
        onPointerState: (state) => cursor?.setState(state),
        onFlipProgress: (progress) => scene.style.setProperty('--flip-progress', progress.toFixed(3)),
        onPointerDown: () => engagement.engage(),
        onPrevAtStart: handlers.onPrevAtStart
      });

      return { camera: cameraController, engagement, bridge };
    },

    setFrameOffset(offset) {
      cameraController?.setFrameOffset(offset);
    },

    /**
     * How much paper is still stacked on each side. Drives the visible page
     * block, so the object thins out as the reader moves through it.
     *
     * @param {{ left: number, right: number }} leaves
     */
    setBlock({ left, right }) {
      const depth = (count) => Math.min(7, count * 0.42).toFixed(2);
      scene.style.setProperty('--edge-left', `${depth(left)}px`);
      scene.style.setProperty('--edge-right', `${depth(right)}px`);
    },

    /** Marks the scene while a leaf is off the block, for the shadow response. */
    setTurning(turning) {
      scene.dataset.turning = String(turning);
      if (turning && !maskFrame) runGutterMask();
      if (!turning) stopGutterMask();
    },

    setTier(nextTier) {
      currentTier = nextTier;
      scene.dataset.tier = nextTier;
      cameraController?.setTier(nextTier);

      startCursor();
      if (!hasFinePointer() && cursor) {
        cursor.destroy();
        cursor = null;
      }
    },

    destroy() {
      stopGutterMask();
      bridge?.destroy();
      cursor?.destroy();
      engagement?.destroy();
      cameraController?.destroy();
      scene.remove();
    }
  };
}
